<template>
  <ModalBase
    :title="title"
    :open="isOpen"
    :icon="icon"
    :hideDivider="true"
    :hideControls="true"
    @close="closeModal"
  >
    <template #content>
      <div class="content">
        <i18n-t keypath="general__text__files-conflict-info" tag="p" class="content__description">
          <template #directoryType>
            <span class="content__description__directory-type">{{ fileTypeLocale }}</span>
          </template>
          <template #name>
            <span class="content__description__name">{{ name }}</span>
          </template>
        </i18n-t>
      </div>
    </template>
    <template #footer>
      <div class="footer-buttons">
        <ButtonOutline :buttonText="t('general__text__cancel')" @onClicked="closeModal" />
        <ButtonOutline
          v-if="type === 'file'"
          :buttonText="t('general__text__replace')"
          @onClicked="replaceItem"
        />
        <ButtonPrimary
          :buttonText="t('general__data__save-copy')"
          :disabled="disableSubmit"
          @onClicked="submit"
        />
      </div>
    </template>
  </ModalBase>
</template>
<script lang="ts" setup>
import ModalBase from './ModalBase.vue'
import ButtonOutline from '@/design-system/atoms/buttons/ButtonOutline.vue'
import ButtonPrimary from '@/design-system/atoms/buttons/ButtonPrimary.vue'
import { computed, onMounted, onUnmounted, ref, toRefs, watch } from 'vue'
import { useI18n } from 'vue-i18n'

type Props = {
  disableSubmit?: boolean
  open?: boolean
  icon?: string
  name: string
  type: 'file' | 'directory' | 'link' //link is not used in this modal but included for consistency
}
const props = withDefaults(defineProps<Props>(), {
  disableSubmit: false,
  open: false,
  icon: undefined
})
const { disableSubmit, name } = toRefs(props)
const emit = defineEmits<{
  (e: 'close'): void
  (e: 'replaceItem'): void
  (e: 'saveCopy'): void
}>()
const { t } = useI18n()
const title = computed(() => {
  return props.type === 'file'
    ? t('general__data__file-already-exists')
    : t('general__text__directory-already-exists')
})
const fileTypeLocale = computed(() => {
  return props?.type === 'file'
    ? t('general__data__file').toLowerCase()
    : t('general__text__directory').toLowerCase()
})

const isOpen = ref(props.open)
watch(
  () => props.open,
  (newValue) => {
    isOpen.value = newValue
  },
  { immediate: true }
)
const closeModal = () => {
  emit('close')
  isOpen.value = false
}
const submit = () => {
  emit('saveCopy')
  isOpen.value = false
}
const replaceItem = () => {
  emit('replaceItem')
  isOpen.value = false
}
onMounted(() => {
  isOpen.value = props.open
})
onUnmounted(() => {
  isOpen.value = false
})
</script>
<style lang="scss" scoped>
@use '@/design-system/design-tokens/variables.scss';
@use '@/design-system/design-tokens/font-mixins.scss';
.content {
  display: flex;
  flex-direction: column;
  margin: 8px 0;
  max-width: 496px;
  width: 100%;
  p {
    color: variables.$color-greyscale-content;
    @include font-mixins.desktop-bold-paragraph;
    margin: 0;
  }
}
.footer-buttons {
  display: flex;
  justify-content: flex-end;
  padding: 16px 0px;
  align-items: center;
  gap: 16px;
  align-self: stretch;
}
</style>
